// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import {
	REASSIGN_INVESTOR_FORM_FIELDS,
	useReassignInvestorModalController,
} from '@dealstack/frontend/admin/pages/Manage/User/ManageInvestor/ReassignInvestorModal/useReassignInvestorModalController';
import { StringInput } from '@dealstack/frontend/shared/components/forms/StringInput/StringInput';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader';
import { DataUserTct } from '@dealstack/shared/lib/generated';
import { Modal } from '@mantine/core';
import { ColumnDef, createColumnHelper, flexRender, Row } from '@tanstack/react-table';
import { memo, ReactNode } from 'react';
import { FormProvider } from 'react-hook-form';
import styled from 'styled-components';

interface Props {
	investorId: string;
	onClose: () => void;
}

const ReassignTSAModal = ({ onClose, investorId }: Props) => {
	const reassignInvestorModalController = useReassignInvestorModalController({ investorId });
	const { tableContainerRef, rows, tanstackTable, rowVirtualizer } =
		reassignInvestorModalController;

	const totalSize = rowVirtualizer.getTotalSize();
	const virtualRows = rowVirtualizer.getVirtualItems();

	const paddingTop = virtualRows?.length > 0 ? virtualRows?.[0]?.start || 0 : 0;
	const paddingBottom =
		virtualRows?.length > 0 ? totalSize - (virtualRows?.[virtualRows.length - 1]?.end || 0) : 0;

	return (
		<Modal fullScreen opened onClose={onClose} title="Reassign Investor to other User">
			<FormProvider {...reassignInvestorModalController.reassignInvestorsForm}>
				<form
					onSubmit={reassignInvestorModalController.reassignInvestorsForm.handleSubmit(
						// eslint-disable-next-line @typescript-eslint/no-empty-function
						() => {}
					)}
				>
					<SearchBox>
						<StringInput
							label="Search query"
							name={REASSIGN_INVESTOR_FORM_FIELDS.SEARCH_QUERY}
						/>
					</SearchBox>
				</form>
			</FormProvider>
			<TableWrapper ref={tableContainerRef}>
				{reassignInvestorModalController.getUsersRequest?.isFetching ? (
					<InlineLoader />
				) : null}
				<table className="table">
					<thead>
						{tanstackTable.getHeaderGroups().map((headerGroup) => (
							<tr key={headerGroup.id}>
								{headerGroup.headers.map((header) => {
									return (
										<th
											key={header.id}
											colSpan={header.colSpan}
											style={{ width: header.getSize() }}
										>
											{header.isPlaceholder ? null : (
												<div
													{...{
														className: header.column.getCanSort()
															? 'cursor-pointer clickAnimation select-none'
															: '',
														onClick:
															header.column.getToggleSortingHandler(),
													}}
												>
													{flexRender(
														header.column.columnDef.header,
														header.getContext()
													)}
													{{
														asc: ' 🔼',
														desc: ' 🔽',
													}[header.column.getIsSorted() as string] ??
														null}
												</div>
											)}
										</th>
									);
								})}
							</tr>
						))}
					</thead>
					<tbody>
						{paddingTop > 0 && (
							<tr>
								<td style={{ height: `${paddingTop}px` }} />
							</tr>
						)}
						{rowVirtualizer.getVirtualItems().map((virtualRow) => {
							const row = rows[virtualRow.index] as Row<DataUserTct>;
							return (
								<tr key={row.id}>
									{row.getVisibleCells().map((cell) => {
										return (
											<td key={cell.id}>
												{flexRender(
													cell.column.columnDef.cell,
													cell.getContext()
												)}
											</td>
										);
									})}
								</tr>
							);
						})}
						{paddingBottom > 0 && (
							<tr>
								<td style={{ height: `${paddingBottom}px` }} />
							</tr>
						)}
					</tbody>
				</table>
			</TableWrapper>
			<br />
			<div>{tanstackTable.getRowModel().rows.length} Rows</div>
		</Modal>
	);
};

const SupportAppBooleanStatus = ({ value }) => <span>{value ? 'Yes' : 'No'}</span>;

const CompactText = styled.span`
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	line-height: 1.4;
	display: block;
`;
const columnHelper = createColumnHelper<DataUserTct>();

export const reassignInvestorColumns = ({
	onReassignInvestor,
}): ColumnDef<DataUserTct, ReactNode>[] => {
	return [
		{
			id: 'dealstackId',
			size: 100,
			accessorFn: (row) => row.id,
			header: () => (
				<CompactText elipsis bold variant="button">
					Dealstack Id
				</CompactText>
			),
			cell: (info) => (
				<CompactText elipsis variant="caption">
					{info.getValue()}
				</CompactText>
			),
		},
		{
			id: 'email',
			accessorFn: (row) => row.email,
			cell: (info) => <span className="support-app-compact-text">{info.getValue()}</span>,
			header: () => <span className="support-app-compact-text">Email</span>,
		},
		{
			id: 'active',
			size: 50,
			accessorFn: (row) => row.active,
			cell: (info) => <SupportAppBooleanStatus value={info.getValue() as boolean} />,
			header: () => <span className="support-app-compact-text">Active</span>,
		},
		{
			id: 'deleted',
			size: 50,
			accessorFn: (row) => row.deleted,
			cell: (info) => <SupportAppBooleanStatus value={info.getValue() as boolean} />,
			header: () => <span className="support-app-compact-text">Deleted</span>,
		},
		{
			id: 'firstName',
			size: 50,
			accessorFn: (row) => row.firstName,
			cell: (info) => <span className="support-app-compact-text">{info.getValue()}</span>,
			header: () => <span className="support-app-compact-text">First Name</span>,
		},
		{
			id: 'lastName',
			size: 50,
			accessorFn: (row) => row.lastName,
			cell: (info) => <span className="support-app-compact-text">{info.getValue()}</span>,
			header: () => <span className="support-app-compact-text">Last Name</span>,
		},
		columnHelper.display({
			id: 'action',
			size: 500,
			header: () => <span className="support-app-compact-text">Actions</span>,
			cell: (info) => {
				return (
					<button onClick={() => onReassignInvestor({ userId: info.row.original.id })}>
						This User
					</button>
				);
			},
		}),
	];
};

const SearchBox = styled.div`
	display: flex;
	align-items: end;
`;

const TableWrapper = styled.div`
	position: relative;
	border: 1px solid lightgray;
	height: 40rem;
	overflow: auto;

	table {
		border-collapse: collapse;
		border-spacing: 0;
		font-family: arial, sans-serif;
		table-layout: fixed;
		width: 100%;
	}

	thead {
		background: white !important;
		margin: 0;
		position: sticky;
		top: 0;
		opacity: 1;
		z-index: 1;
	}

	th {
		border-bottom: 1px solid lightgray;
		border-right: 1px solid lightgray;
		padding: 2px 4px;
		text-align: left;
	}

	td {
		padding: 6px;
	}
`;
export const ReassignInvestorModal = memo(ReassignTSAModal);
